// Presentation only: the display's already-synced countdown window is authoritative.
export const SOUND_PREFERENCE_KEY = "brainbattle.display.soundEnabled";
export const gameAudioFlags = {
  enabled: ["1", "true", "yes", "on"].includes(
    String(process.env.REACT_APP_ENABLE_GAME_AUDIO || "").trim().toLowerCase(),
  ),
};
// One-shot clips start with their stage. public/audio/round_intro.mp3 is 3.840
// seconds, public/audio/answer_reveal.mp3 is 5.120 seconds and
// public/audio/game-over.mp3 is 15.647 seconds; the tail lets a clip's own
// `ended` event finish it.
export const ROUND_INTRO_SOUND_DURATION_MS = 3840;
export const ANSWER_REVEAL_SOUND_DURATION_MS = 5120;
export const GAME_OVER_SOUND_DURATION_MS = 15647;
export const SOUND_CLIP_TAIL_MS = 500;

// Plays one clip inside one synced window. Used for the countdown and each one-shot clip.
export function createQuestionTimerAudio(audio) {
  let countdownWindow = null;
  let soundEnabled = true;
  let unlocked = false;
  let pending = false;
  let playing = false;
  let failed = false;
  let disposed = false;
  let suspended = document.hidden;
  let completedKey = null;
  let boundaryTimer = null;
  let stateVersion = 0;

  audio.preload = "auto";
  audio.loop = false;

  const pause = () => {
    try { audio.pause(); } catch { /* Audio is optional. */ }
    playing = false;
  };
  const clearBoundary = () => {
    clearTimeout(boundaryTimer);
    boundaryTimer = null;
  };
  const position = () => Math.max(0, (Date.now() - countdownWindow.startTime) / 1000);
  const seek = (seconds) => {
    try { audio.currentTime = seconds; return true; } catch { return false; }
  };

  const reconcile = () => {
    clearBoundary();
    if (disposed) return;
    const now = Date.now();
    if (!countdownWindow || !soundEnabled || !unlocked || failed || suspended || document.hidden) {
      pause();
      return;
    }
    if (now >= countdownWindow.endTime || completedKey === countdownWindow.key ||
        (Number.isFinite(audio.duration) && position() >= audio.duration)) {
      pause();
      return;
    }
    if (now < countdownWindow.startTime) {
      pause();
      boundaryTimer = setTimeout(reconcile, countdownWindow.startTime - now);
      return;
    }
    boundaryTimer = setTimeout(reconcile, countdownWindow.endTime - now);
    // Metadata is required to seek safely. canplay/loadedmetadata will retry.
    if (pending || audio.readyState < 1) return;
    if (playing) {
      if (Math.abs(audio.currentTime - position()) > 0.5) seek(position());
      return;
    }
    if (!seek(position())) return;
    const playStateVersion = stateVersion;
    const onPlayFailure = (error) => {
      pending = false;
      // pause() may abort a pending play during backgrounding or stage changes.
      // Only a permission denial invalidates the successful gesture unlock.
      if (error?.name === "NotAllowedError") unlocked = false;
      pause();
      // Resume/new-window events may have arrived while the old play was pending.
      // Retry that changed state once; unchanged failures wait for the next event.
      if (stateVersion !== playStateVersion) reconcile();
    };
    pending = true;
    try {
      Promise.resolve(audio.play()).then(() => {
        pending = false;
        if (disposed) { pause(); return; }
        playing = true;
        reconcile();
      }, onPlayFailure);
    } catch (error) {
      onPlayFailure(error);
    }
  };

  const unlock = () => {
    if (disposed || failed || !soundEnabled || pending || unlocked) return;
    // Call play in the gesture itself. Prime silently, then reuse this element.
    pending = true;
    audio.muted = true;
    try {
      Promise.resolve(audio.play()).then(() => {
        pause();
        audio.muted = false;
        pending = false;
        if (disposed) return;
        unlocked = true;
        reconcile();
      }, () => {
        pause();
        audio.muted = false;
        pending = false;
      });
    } catch {
      audio.muted = false;
      pending = false;
      pause();
    }
  };
  const onEnded = () => {
    completedKey = countdownWindow?.key;
    playing = false;
    clearBoundary();
  };
  const onError = () => { failed = true; clearBoundary(); pause(); };
  const onVisibility = () => { stateVersion += 1; suspended = document.hidden; reconcile(); };
  const onPageHide = () => { stateVersion += 1; suspended = true; reconcile(); };
  const onPageShow = () => { stateVersion += 1; suspended = document.hidden; reconcile(); };
  audio.addEventListener("loadedmetadata", reconcile);
  audio.addEventListener("canplay", reconcile);
  audio.addEventListener("playing", reconcile); // Re-seek after buffering, without polling.
  audio.addEventListener("ended", onEnded);
  audio.addEventListener("error", onError);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);

  return {
    unlock,
    setEnabled(enabled) { stateVersion += 1; soundEnabled = enabled; reconcile(); },
    setWindow(next) {
      stateVersion += 1;
      if (next?.key !== countdownWindow?.key) { pause(); seek(0); completedKey = null; }
      countdownWindow = next;
      reconcile();
    },
    dispose() {
      disposed = true;
      clearBoundary();
      pause();
      audio.removeEventListener("loadedmetadata", reconcile);
      audio.removeEventListener("canplay", reconcile);
      audio.removeEventListener("playing", reconcile);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    },
  };
}
